/**
 * Address autocomplete via Google Places. Same onSelect shape as AddressDaDataField:
 * { address, latitude, longitude }.
 */
import * as React from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import { useGoogleMaps } from '../GoogleMapsProvider';

export default function AddressPlacesField({
  value,
  onChange,
  onSelect,
  error,
  helperText,
  label,
  placeholder,
  disabled,
}) {
  const inputRef = React.useRef(null);
  const { isLoaded } = useGoogleMaps();

  React.useEffect(() => {
    if (!isLoaded || !inputRef.current || !window.google?.maps?.places?.Autocomplete) {
      return undefined;
    }
    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      fields: ['formatted_address', 'geometry'],
    });
    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place) return;
      const address = place.formatted_address || '';
      const loc = place.geometry?.location;
      if (onChange) onChange(address);
      if (onSelect) {
        onSelect({
          address,
          latitude: loc ? String(loc.lat()) : '',
          longitude: loc ? String(loc.lng()) : '',
        });
      }
    });
    return () => {
      if (listener) listener.remove();
    };
  }, [isLoaded, onChange, onSelect]);

  return (
    <TextField
      fullWidth
      inputRef={inputRef}
      label={label}
      placeholder={placeholder ?? label}
      value={value ?? ''}
      onChange={(e) => onChange && onChange(e.target.value)}
      error={!!error}
      helperText={helperText ?? ' '}
      disabled={disabled || !isLoaded}
      autoComplete="off"
    />
  );
}

AddressPlacesField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};
